import type {
  StripeAccountSettings,
  StripeCheckoutSettings,
} from "schemas/generated/stripeConnect";

import * as React from "react";

import Input from "@common/designSystem/Input";
import LabeledControl from "@common/designSystem/LabeledControl";
import LogoBadge from "@common/LogoBadge";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { integrationRouter, integrationUtils } from "@editor/trpc/integration";
import {
  stripeConnectRouter,
  stripeConnectUtils,
} from "@editor/trpc/stripeConnect";

import {
  errorToast,
  successToast,
} from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Checkbox } from "@replo/design-system/components/checkbox/Checkbox";
import { CheckboxWithDescription } from "@replo/design-system/components/checkbox/CheckboxWithDescription";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link2Off,
} from "lucide-react";
import { useLocation } from "react-router-dom";

// Shipping address collection component
const ShippingAddressCollection: React.FC<{
  allowedCountries: string[] | null;
  onChange: (countries: string[] | null) => void;
}> = ({ allowedCountries, onChange }) => {
  const countries = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "GB", name: "United Kingdom" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "IT", name: "Italy" },
    { code: "ES", name: "Spain" },
    { code: "NL", name: "Netherlands" },
    { code: "SE", name: "Sweden" },
    { code: "NO", name: "Norway" },
    { code: "DK", name: "Denmark" },
    { code: "FI", name: "Finland" },
    { code: "JP", name: "Japan" },
    { code: "KR", name: "South Korea" },
    { code: "SG", name: "Singapore" },
    { code: "HK", name: "Hong Kong" },
    { code: "NZ", name: "New Zealand" },
    { code: "CH", name: "Switzerland" },
    { code: "AT", name: "Austria" },
    { code: "BE", name: "Belgium" },
    { code: "IE", name: "Ireland" },
    { code: "PT", name: "Portugal" },
    { code: "PL", name: "Poland" },
    { code: "CZ", name: "Czech Republic" },
    { code: "HU", name: "Hungary" },
    { code: "SK", name: "Slovakia" },
    { code: "SI", name: "Slovenia" },
    { code: "HR", name: "Croatia" },
    { code: "RO", name: "Romania" },
    { code: "BG", name: "Bulgaria" },
    { code: "GR", name: "Greece" },
    { code: "CY", name: "Cyprus" },
    { code: "MT", name: "Malta" },
    { code: "LU", name: "Luxembourg" },
    { code: "EE", name: "Estonia" },
    { code: "LV", name: "Latvia" },
    { code: "LT", name: "Lithuania" },
  ];

  const selectedCountries = allowedCountries ?? [];
  const isCollecting = selectedCountries.length > 0;

  const handleCountryToggle = (countryCode: string, checked: boolean) => {
    if (checked) {
      const newCountries = [...selectedCountries, countryCode];
      onChange(newCountries.length === 0 ? null : newCountries);
    } else {
      const newCountries = selectedCountries.filter(
        (code) => code !== countryCode,
      );
      onChange(newCountries.length === 0 ? null : newCountries);
    }
  };

  const handleDontCollectToggle = (checked: boolean) => {
    if (checked) {
      onChange(null);
    } else {
      onChange([]);
    }
  };

  const getDisplayText = () => {
    if (!isCollecting) {
      return "Don't collect";
    }
    if (selectedCountries.length === 1) {
      const country = countries.find((c) => c.code === selectedCountries[0]);
      return country?.name ?? "1 country selected";
    }
    return `${selectedCountries.length} countries selected`;
  };

  return (
    <Combobox.Root options={[]}>
      <Combobox.Trigger>
        <Combobox.SelectionButton
          title={getDisplayText()}
          placeholder="Don't collect"
          size="base"
        />
      </Combobox.Trigger>
      <Combobox.Popover>
        <Combobox.Content areOptionsSearchable={false} emptySearchMessage="">
          <div className="space-y-3 p-2">
            <CheckboxWithDescription
              size="base"
              label="Do not collect shipping address"
              description="Skip shipping address collection during checkout"
              checked={!isCollecting}
              onChange={handleDontCollectToggle}
            />

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <div key={country.code} className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectedCountries.includes(country.code)}
                    onChange={(checked) =>
                      handleCountryToggle(country.code, checked)
                    }
                  />
                  <span className="text-sm">{country.name}</span>
                </div>
              ))}
            </div>
          </div>
        </Combobox.Content>
      </Combobox.Popover>
    </Combobox.Root>
  );
};

// Settings form component
const SettingsPanel: React.FC<{
  settings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  } | null;
  onUpdateSettings: (settings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  }) => void;
  isLoading: boolean;
}> = ({ settings, onUpdateSettings, isLoading }) => {
  const [isCheckoutSettingsExpanded, setIsCheckoutSettingsExpanded] =
    React.useState(false);
  const [localSettings, setLocalSettings] = React.useState<{
    accountSettings: Partial<StripeAccountSettings>;
    checkoutSettings: Partial<StripeCheckoutSettings>;
  }>({
    accountSettings: settings?.accountSettings ?? {},
    checkoutSettings: settings?.checkoutSettings ?? {
      automaticTax: false,
      allowPromotionCodes: false,
    },
  });

  React.useEffect(() => {
    setLocalSettings({
      accountSettings: settings?.accountSettings ?? {},
      checkoutSettings: settings?.checkoutSettings ?? {
        automaticTax: false,
        allowPromotionCodes: false,
      },
    });
  }, [settings]);

  const handleSave = () => {
    onUpdateSettings({
      accountSettings: {
        ...localSettings.accountSettings,
        payoutSchedule: {
          interval: "monthly",
          monthlyAnchor: 1,
        },
      } as StripeAccountSettings,
      checkoutSettings:
        localSettings.checkoutSettings as StripeCheckoutSettings,
    });
  };

  const updateAccountSetting = (key: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      accountSettings: {
        ...prev.accountSettings,
        [key]: value,
      },
    }));
  };

  const updateCheckoutSetting = (key: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      checkoutSettings: {
        ...prev.checkoutSettings,
        [key]: value,
      },
    }));
  };

  return (
    <div className="space-y-4">
      {/* Settings Collapsible Section */}
      <div className="border-0.5 border-border rounded-xl">
        <button
          type="button"
          className="w-full flex items-center justify-between p-4 text-left hover:bg-sheet transition-colors"
          onClick={() =>
            setIsCheckoutSettingsExpanded(!isCheckoutSettingsExpanded)
          }
        >
          <h4 className="text-base font-medium">Checkout settings</h4>
          {isCheckoutSettingsExpanded ? (
            <ChevronUp size={16} className="text-muted" />
          ) : (
            <ChevronDown size={16} className="text-muted" />
          )}
        </button>

        {isCheckoutSettingsExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Statement Descriptor */}
            <LabeledControl
              label="Statement Descriptor"
              description="Will appear on customers bank statements"
              size="base"
            >
              <Input
                value={localSettings.accountSettings?.statementDescriptor ?? ""}
                onChange={(e) =>
                  updateAccountSetting("statementDescriptor", e.target.value)
                }
                placeholder="Studs"
                maxLength={22}
                size="base"
              />
            </LabeledControl>

            {/* Collect address at checkout */}
            <LabeledControl label="Collect address at checkout" size="base">
              <ShippingAddressCollection
                allowedCountries={
                  localSettings.checkoutSettings?.allowedCountries ?? null
                }
                onChange={(countries) =>
                  updateCheckoutSetting("allowedCountries", countries)
                }
              />
            </LabeledControl>

            {/* Automatic Tax */}
            <CheckboxWithDescription
              size="base"
              label="Enable automatic tax calculation"
              description="Will automatically calculate tax based on customer location"
              checked={localSettings.checkoutSettings?.automaticTax ?? false}
              onChange={(checked) =>
                updateCheckoutSetting("automaticTax", checked)
              }
            />

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                variant="primary"
                size="base"
                onClick={handleSave}
                isLoading={isLoading}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const StripeConnectIntegration: React.FC<{
  variant?: "black-box" | "white-box";
}> = ({ variant = "white-box" }) => {
  const projectId = useCurrentProjectId();
  const location = useLocation();
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [settings, setSettings] = React.useState<{
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  } | null>(null);

  // Get Stripe Connect onboarding status
  const { data: onboardingStatus, isLoading } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  // Get all integrations to find the Stripe Connect integration ID
  const { data: integrations } = integrationRouter.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  // Find the Stripe Connect integration
  const stripeIntegration = integrations?.find(
    (integration) => integration.type === "StripeConnect",
  );

  // Get Stripe Connect settings
  const { data: stripeSettings } =
    stripeConnectRouter.stripeConnectGetSettings.useQuery(
      projectId ? { projectId } : skipToken,
    );

  // Create onboarding link mutation
  const createOnboardingLink =
    stripeConnectRouter.stripeConnectCreateOnboardingLink.useMutation({
      onSuccess: (data) => {
        window.open(data.onboardingUrl, "_blank");
        setIsConnecting(true);
      },
      onError: () => {
        setIsConnecting(false);
        errorToast(
          "Setup Failed",
          "Failed to create Stripe Connect onboarding link.",
        );
      },
    });

  // Create finish setup link mutation
  const createFinishSetupLink =
    stripeConnectRouter.stripeConnectCreateFinishSetupLink.useMutation({
      onSuccess: (data) => {
        window.open(data.finishSetupUrl, "_blank");
        setIsConnecting(true);
        successToast(
          "Stripe Connect Setup",
          "Redirected to Stripe to complete setup.",
        );
      },
      onError: () => {
        setIsConnecting(false);
        errorToast(
          "Setup Failed",
          "Failed to create Stripe Connect finish setup link.",
        );
      },
    });

  // Create dashboard login link mutation
  const createDashboardLoginLink =
    stripeConnectRouter.stripeConnectCreateDashboardLoginLink.useMutation({
      onSuccess: (data) => {
        window.open(data.dashboardUrl, "_blank");
      },
      onError: () => {
        errorToast(
          "Dashboard Access Failed",
          "Failed to create Stripe dashboard login link.",
        );
      },
    });

  // Refresh onboarding status mutation
  const refreshOnboardingStatus =
    stripeConnectRouter.stripeConnectRefreshOnboardingStatus.useMutation({
      onSuccess: (data) => {
        if (data.wasUpdated && data.onboardingCompleted) {
          // Only invalidate and show success when onboarding is actually completed
          stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
          successToast(
            "Setup Complete!",
            "Stripe Connect onboarding has been completed successfully.",
          );
        } else if (data.wasUpdated) {
          // Status was updated but still not complete - just invalidate without toast
          stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
        }
        // If no update, don't invalidate to prevent unnecessary refetches
      },
      onError: () => {
        errorToast(
          "Status Check Failed",
          "Failed to refresh Stripe Connect status. Please try again.",
        );
      },
    });

  // Delete integration mutation
  const deleteIntegration = integrationRouter.delete.useMutation({
    onSuccess: () => {
      successToast(
        "Stripe Disconnected",
        "Stripe Connect has been successfully disconnected from your project.",
      );
      // Invalidate queries to refresh the UI
      integrationUtils.find.invalidate();
      stripeConnectUtils.stripeConnectGetOnboardingStatus.invalidate();
    },
    onError: () => {
      errorToast(
        "Disconnect Failed",
        "Failed to disconnect Stripe Connect. Please try again.",
      );
    },
  });

  // Update settings mutation
  const updateSettings =
    stripeConnectRouter.stripeConnectUpdateSettings.useMutation({
      onSuccess: () => {
        successToast(
          "Settings Updated",
          "Stripe Connect settings have been updated successfully.",
        );
        stripeConnectUtils.stripeConnectGetSettings.invalidate();
      },
      onError: () => {
        errorToast(
          "Update Failed",
          "Failed to update Stripe Connect settings. Please try again.",
        );
      },
    });

  const handleConnect = () => {
    setIsConnecting(true);
    createOnboardingLink.mutate({ projectId: projectId! });
  };

  const handleFinishSetup = () => {
    setIsConnecting(true);
    createFinishSetupLink.mutate({ projectId: projectId! });
  };

  const handleOpenDashboard = () => {
    createDashboardLoginLink.mutate({ projectId: projectId! });
  };

  const handleDisconnect = () => {
    if (!stripeIntegration || !projectId) {
      return;
    }

    deleteIntegration.mutate({
      integrationId: stripeIntegration.id,
      projectId: projectId,
    });
  };

  const handleUpdateSettings = (newSettings: {
    accountSettings?: StripeAccountSettings;
    checkoutSettings?: StripeCheckoutSettings;
  }) => {
    if (!projectId) {
      return;
    }

    updateSettings.mutate({
      projectId,
      accountSettings: newSettings.accountSettings,
      checkoutSettings: newSettings.checkoutSettings,
    });
  };

  // Update settings state when data loads
  React.useEffect(() => {
    if (stripeSettings) {
      setSettings(stripeSettings);
    }
  }, [stripeSettings]);

  // Auto-refresh onboarding status once when component mounts if partially connected
  const hasAutoRefreshed = React.useRef(false);

  React.useEffect(() => {
    if (
      projectId &&
      onboardingStatus?.isConnected &&
      !onboardingStatus?.onboardingCompleted &&
      !isRefreshing &&
      !isConnecting &&
      !hasAutoRefreshed.current &&
      !refreshOnboardingStatus.isPending
    ) {
      hasAutoRefreshed.current = true;
      setIsRefreshing(true);
      refreshOnboardingStatus.mutate(
        { projectId },
        {
          onSettled: () => setIsRefreshing(false),
        },
      );
    }
  }, [
    projectId,
    onboardingStatus?.isConnected,
    onboardingStatus?.onboardingCompleted,
    isRefreshing,
    isConnecting,
    refreshOnboardingStatus,
  ]);

  if (isLoading || isRefreshing) {
    return <Skeleton className="h-[74px] w-full" />;
  }

  const isConnected = onboardingStatus?.isConnected ?? false;
  const isOnboardingComplete = onboardingStatus?.onboardingCompleted ?? false;

  const isSettingsContext = location.pathname.includes("/settings/");

  // For black box variant when not connected, show simple setup UI
  if (variant === "black-box" && !isConnected) {
    return (
      <div className="flex items-center justify-between p-4 border-0.5 border-border rounded-xl bg-black text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
            <LogoBadge className="w-full h-full" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-medium text-white">
              {isSettingsContext ? "Replo" : "Set up payments"}
            </span>
            <span className="text-sm text-sheet">
              Host products, sell them, and get paid through Replo.
            </span>
          </div>
        </div>
        <Button
          variant="secondary"
          size="base"
          onClick={handleConnect}
          isLoading={
            isConnecting || createOnboardingLink.isPending || isRefreshing
          }
          endEnhancer={<ArrowUpRight size={16} />}
          UNSAFE_className="bg-white text-black hover:bg-gray-100"
        >
          Set up
        </Button>
      </div>
    );
  }

  // For black box variant when partially connected, show white box with limited controls
  if (variant === "black-box" && isConnected && !isOnboardingComplete) {
    return (
      <div className="flex items-center justify-between p-4 border-0.5 border-border rounded-xl bg-white">
        <div className="flex items-center gap-3">
          <div className="border-0.5 border-border rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
            <LogoBadge className="w-full h-full" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-medium">Pay on Replo</span>
            <span className="text-sm text-default">
              Setup incomplete - finish onboarding to accept payments
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          size="base"
          onClick={handleFinishSetup}
          isLoading={
            isConnecting || createFinishSetupLink.isPending || isRefreshing
          }
        >
          Complete Setup
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isConnected ? (
        <>
          {/* Replo Integration Card */}
          <div className="p-4 border-0.5 border-border rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="border-0.5 border-border rounded-2xl w-10 h-10 shrink-0 overflow-hidden">
                  <LogoBadge className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-base font-medium">
                    {isSettingsContext ? "Replo" : "Set up payments"}
                  </span>
                  <span className="text-sm text-muted">
                    Host products, sell them, and get paid through Replo.
                  </span>
                </div>
              </div>
              <Button
                variant="secondary"
                size="base"
                endEnhancer={<ExternalLink size={12} />}
                onClick={handleOpenDashboard}
                isLoading={createDashboardLoginLink.isPending}
              >
                Manage Payments
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted">
                {isOnboardingComplete
                  ? "Ready to accept payments"
                  : "Setup incomplete - finish onboarding to accept payments"}
              </span>
              <div className="flex items-center gap-2">
                <IconButton
                  variant="secondary"
                  icon={<Link2Off size={12} />}
                  size="sm"
                  tooltipText="Disconnect Stripe"
                  onClick={handleDisconnect}
                  isLoading={deleteIntegration.isPending}
                />
                <Tag
                  variant={isOnboardingComplete ? "success" : "info"}
                  size="base"
                >
                  {isOnboardingComplete ? "Connected" : "Setup Required"}
                </Tag>
              </div>
            </div>

            {!isOnboardingComplete && (
              <div className="flex justify-start mt-3">
                <Button
                  variant="primary"
                  size="base"
                  onClick={handleFinishSetup}
                  isLoading={
                    isConnecting ||
                    createFinishSetupLink.isPending ||
                    isRefreshing
                  }
                >
                  Complete Setup
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="p-4 border-0.5 border-border rounded-xl bg-surface">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-base font-medium">
                  Connect Stripe Account
                </span>
                <span className="text-sm text-sheet">
                  Set up Stripe to start accepting payments on your pages.
                  You'll be redirected to Stripe to complete the setup process.
                </span>
              </div>

              <div className="flex justify-start">
                <Button
                  variant="primary"
                  size="base"
                  onClick={handleConnect}
                  isLoading={
                    isConnecting ||
                    createOnboardingLink.isPending ||
                    isRefreshing
                  }
                >
                  Connect Stripe
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Checkout Settings - Separate card below Replo integration */}
      <SettingsPanel
        settings={settings}
        onUpdateSettings={handleUpdateSettings}
        isLoading={updateSettings.isPending}
      />
    </div>
  );
};
